import { Stack } from '@mui/material';
import ProductGroupSelect from 'components/ProductGroupSelect/ProductGroupSelect';
import { SWR_PRODUCT_GROUPS_ENDPOINT } from 'hooks/swr/useProductGroups';
import useResponsive from 'hooks/useResponsive';
import { isArray, isNil } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import * as productGroupService from 'services/productGroupService';
import { mutate } from 'swr';
import GroupsToolbar from './GroupsToolbar';
import type { ProductGroupSelectedNode } from 'components/ProductGroupSelect/ProductGroupSelect';
import type { ProductGroup } from 'models/product/productGroup';
import type { FC, SyntheticEvent } from 'react';

interface ProductGroupsProps {
  onGroupSelect: (group?: ProductGroupSelectedNode) => void;
  selectedGroup?: ProductGroupSelectedNode;
  onChange?: VoidFunction;
}

const ProductGroups: FC<ProductGroupsProps> = observer(
  ({ onGroupSelect, selectedGroup, onChange }) => {
    const [expanded, setExpanded] = useState<string[]>([]);
    const isSmallScreen = useResponsive('down', 'md');

    const resetGroupsData = async (): Promise<unknown> =>
      await mutate(
        (key) => isArray(key) && key[0] === SWR_PRODUCT_GROUPS_ENDPOINT,
        undefined
      );

    const handleAddIn = (name: string): void => {
      productGroupService
        .createProductGroupAsync({
          name,
          parentId: selectedGroup?.id,
        })
        .then(async () => {
          onChange?.();

          return await resetGroupsData();
        })
        .catch((err) => {
          throw err;
        });
    };

    const handleAddOut = (name: string): void => {
      productGroupService
        .createProductGroupAsync({
          name,
          parentId: selectedGroup?.parentId,
        })
        .then(async () => {
          onChange?.();

          return await resetGroupsData();
        })
        .catch((err) => {
          throw err;
        });
    };

    const handleNodeSelect = (node?: ProductGroupSelectedNode): void => {
      const group = isNil(node) || node.id <= 0 ? undefined : node;
      onGroupSelect(group);
    };

    const handleOnDelete = (): void => {
      if (isNil(selectedGroup)) {
        return;
      }

      productGroupService
        .deleteProductGroupAsync(selectedGroup?.id)
        .then(async () => {
          onChange?.();

          return await resetGroupsData();
        })
        .catch((err) => {
          throw err;
        });
    };

    const handleMove = (destinationGroupId?: number): void => {
      if (isNil(selectedGroup)) {
        return;
      }

      productGroupService
        .saveProductGroupAsync(selectedGroup.id, {
          name: selectedGroup.name,
          parentId: destinationGroupId,
        })
        .then(async () => {
          onChange?.();

          return await resetGroupsData();
        })
        .catch((err) => {
          throw err;
        });
    };

    const handleRename = (name: string): void => {
      if (isNil(selectedGroup)) {
        return;
      }

      productGroupService
        .saveProductGroupAsync(selectedGroup.id, {
          name,
          parentId: selectedGroup.parentId,
        })
        .then(async () => {
          onChange?.();

          return await resetGroupsData();
        })
        .catch((err) => {
          throw err;
        });
    };

    const handleToggle = (_event: SyntheticEvent, itemIds: string[]): void => {
      setExpanded(itemIds);
    };

    const dataPostProcessor = (data: ProductGroup[]): ProductGroup[] => [
      { id: -1, name: 'Товары без группы', groups: [] },
      ...(data ?? []),
    ];

    return (
      <Stack spacing={2}>
        {!isSmallScreen && (
          <GroupsToolbar
            selectedNode={selectedGroup}
            onAddIn={handleAddIn}
            onAddOut={handleAddOut}
            onDelete={handleOnDelete}
            onMove={handleMove}
            onRename={handleRename}
          />
        )}
        <ProductGroupSelect
          expandedItems={expanded}
          onExpandedItemsChange={handleToggle}
          selectedItems={selectedGroup?.id.toString() ?? '-1'}
          dataPostprocessor={dataPostProcessor}
          onSelectedItemsChange={handleNodeSelect}
        />
      </Stack>
    );
  }
);

export default ProductGroups;
